import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import './Layout.css';

/* ── Inline SVG nav icons (20×20, currentColor) ── */
const UnderwritingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2h8l4 4v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
    <path d="M12 2v4h4" />
    <circle cx="10" cy="12" r="3" />
    <path d="M12.1 14.1L15 17" />
  </svg>
);

const DistributionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="4" r="2" />
    <circle cx="4" cy="16" r="2" />
    <circle cx="16" cy="16" r="2" />
    <path d="M10 6v4" />
    <path d="M10 10l-5 4" />
    <path d="M10 10l5 4" />
  </svg>
);

const ProductsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="2" width="14" height="5" rx="1" />
    <rect x="3" y="9" width="14" height="5" rx="1" />
    <rect x="5" y="16" width="10" height="2" rx="0.5" />
  </svg>
);

const PASIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 2h7l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
    <path d="M12 2v4h4" />
    <path d="M7 10h6" />
    <path d="M7 13h4" />
    <path d="M10 7l2 2-2 2" />
  </svg>
);

const LeadershipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17V9l4-3v11" />
    <path d="M8 17V6l4-4v15" />
    <path d="M13 17V8l4 3v6" />
    <line x1="2" y1="17" x2="18" y2="17" />
  </svg>
);

const ResourcesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4a1 1 0 0 1 1-1h4l1 1h7a1 1 0 0 1 1 1v2H3V4z" />
    <rect x="3" y="7" width="14" height="10" rx="1" />
    <path d="M7 11h6" />
    <path d="M7 14h4" />
  </svg>
);

const BrokerPortalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="16" height="14" rx="2" />
    <path d="M2 8h16" />
    <path d="M8 8v9" />
  </svg>
);

/* ── Theme toggle icons ── */
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13.5 8.5a5.5 5.5 0 0 1-7-7 5.5 5.5 0 1 0 7 7z" />
  </svg>
);

/* ── NavTab component ── */
function NavTab({ to, icon, label, isActive }) {
  return (
    <Link
      to={to}
      className={`nav-tab ${isActive ? 'nav-tab--active' : ''}`}
    >
      <span className="nav-tab-icon">{icon}</span>
      <span className="nav-tab-label">{label}</span>
    </Link>
  );
}

export function Layout({ children }) {
  const { user, logout, hasGroup, hasAnyGroup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  /* Theme state — default dark, persisted in localStorage */
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('dex-theme') || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('dex-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  /* Determine which top-level tab is active based on current path */
  const getActiveTab = useCallback(() => {
    const p = location.pathname;
    if (p === '/broker' || p.startsWith('/broker/')) return 'broker';
    if (p === '/distribution' || p === '/brokers' || p.startsWith('/brokers/')) return 'distribution';
    if (p === '/products' || p.startsWith('/products/') || p === '/approvals' || p === '/claims') return 'products';
    if (p === '/pas' || p === '/policies' || p.startsWith('/policies/') || p === '/commissions' || p === '/compliance' || p === '/treaties') return 'pas';
    if (p === '/leadership' || p === '/audit-trail' || p === '/capacity-partners') return 'leadership';
    if (p === '/resources' || p === '/docs' || p === '/api-docs') return 'resources';
    // Default: underwriting
    return 'underwriting';
  }, [location.pathname]);

  const activeTab = getActiveTab();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isInternalUser = hasAnyGroup(['underwriter', 'leadership']);
  const isBroker = hasGroup('broker');

  return (
    <div className="layout">
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen((p) => !p)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <img src="/dex-engine-logo.svg" alt="DEX Engine" className="sidebar-logo-img" />
          <span className="sidebar-env">MGA Platform</span>
        </div>

        <nav className="sidebar-nav">
          {isInternalUser && (
            <>
              <NavTab
                to="/"
                icon={<UnderwritingIcon />}
                label="Underwriting"
                isActive={activeTab === 'underwriting'}
              />
              <NavTab
                to="/distribution"
                icon={<DistributionIcon />}
                label="Distribution"
                isActive={activeTab === 'distribution'}
              />
              <NavTab
                to="/products"
                icon={<ProductsIcon />}
                label="Products"
                isActive={activeTab === 'products'}
              />
              <NavTab
                to="/pas"
                icon={<PASIcon />}
                label="PAS"
                isActive={activeTab === 'pas'}
              />
            </>
          )}

          {hasGroup('leadership') && (
            <NavTab
              to="/leadership"
              icon={<LeadershipIcon />}
              label="Leadership"
              isActive={activeTab === 'leadership'}
            />
          )}

          {!hasGroup('leadership') && hasGroup('capacity-provider') && (
            <NavTab
              to="/capacity-partners"
              icon={<LeadershipIcon />}
              label="Capacity"
              isActive={activeTab === 'leadership'}
            />
          )}

          {isBroker && (
            <NavTab
              to="/broker"
              icon={<BrokerPortalIcon />}
              label="Broker Portal"
              isActive={activeTab === 'broker'}
            />
          )}

          {isInternalUser && (
            <NavTab
              to="/resources"
              icon={<ResourcesIcon />}
              label="Resources"
              isActive={activeTab === 'resources'}
            />
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-name">{user?.displayName || user?.email}</span>
            <span className="user-role">
              {user?.groups?.[0] || 'user'}
            </span>
          </div>
          <div className="sidebar-footer-row">
            <button className="btn btn--secondary btn--sm" onClick={handleLogout}>
              Sign Out
            </button>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
}
